import { useState, type ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Upload, AlertCircle } from 'lucide-react'
import { fetchMinimalClients } from '@frontend/lib/api'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@frontend/components/ui/dialog'
import { Button } from '@frontend/components/ui/button'
import { Spinner } from '@frontend/components/ui/spinner'
import { MultiSelect } from '@frontend/components/ui/multi-select'

export interface ClientSelection {
  clientId?: string
  clientName: string
}

type DialogState = 'form' | 'submitting' | 'results'

interface BulkUploadToClientsDialogBaseProps<TResults> {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Optional callback fired once when the dialog closes after results are shown. */
  onComplete?: () => void

  /** Header content. */
  title: string
  description: string

  /** Items being acted on; rendered as a chip list above the client picker. */
  selectedItems?: { id: string; name: string }[]
  /** Heading above the chip list, e.g. "Selected Vendors" / "Selected Risks". */
  selectedItemsLabel?: string

  /** Optional extra form fields (above/below the client picker). */
  formExtras?: ReactNode

  /** If true, the multiselect lets users type ad-hoc client names. */
  allowCreateClients?: boolean

  /** Called when the user clicks the action button. */
  submit: (clients: ClientSelection[]) => Promise<TResults>

  /** Render results body once `submit` resolves. */
  renderResults: (results: TResults) => ReactNode

  /** Cache keys to invalidate on success. */
  invalidateOnSuccess?: string[][]

  /** Action label. Defaults to "Upload". The base appends client count. */
  actionVerb?: string

  /** Message shown during submission. */
  submittingMessage?: ReactNode
}

/**
 * Shared 3-state dialog (form → submitting → results) for pushing a selection
 * of items to one or more clients. Owns the client multi-select, dialog
 * scaffolding, mutation lifecycle, and reset-on-close behavior. The caller
 * supplies the items being acted on (via `selectedItemsSummary`), the submit
 * function, and the results renderer.
 */
export function BulkUploadToClientsDialogBase<TResults>({
  open,
  onOpenChange,
  onComplete,
  title,
  description,
  selectedItems,
  selectedItemsLabel,
  formExtras,
  allowCreateClients,
  submit,
  renderResults,
  invalidateOnSuccess,
  actionVerb = 'Upload',
  submittingMessage,
}: BulkUploadToClientsDialogBaseProps<TResults>) {
  const queryClient = useQueryClient()

  const [selectedClientIds, setSelectedClientIds] = useState<string[]>([])
  const [manualClientNames, setManualClientNames] = useState<string[]>([])
  const [dialogState, setDialogState] = useState<DialogState>('form')
  const [results, setResults] = useState<TResults | null>(null)

  const { data: clientsData, isLoading: clientsLoading } = useQuery({
    queryKey: ['clients'],
    queryFn: fetchMinimalClients,
    enabled: open,
  })

  const clients = clientsData?.clients || []
  const clientOptions = clients.map((c) => ({ label: c.name, value: c.id }))

  const submitMutation = useMutation({
    mutationFn: submit,
    onMutate: () => setDialogState('submitting'),
    onSuccess: (data) => {
      setResults(data)
      setDialogState('results')
      for (const key of invalidateOnSuccess ?? []) {
        queryClient.invalidateQueries({ queryKey: key })
      }
    },
    onError: () => setDialogState('form'),
  })

  const totalClientCount = selectedClientIds.length + manualClientNames.length

  const handleSubmit = () => {
    if (totalClientCount === 0) return
    const clientsToSubmit: ClientSelection[] = [
      ...selectedClientIds.map((id) => {
        const c = clients.find((x) => x.id === id)
        return { clientId: id, clientName: c?.name || '' }
      }),
      ...manualClientNames.map((name) => ({ clientName: name })),
    ]
    submitMutation.mutate(clientsToSubmit)
  }

  const handleClose = () => {
    if (dialogState === 'results') onComplete?.()
    setDialogState('form')
    setSelectedClientIds([])
    setManualClientNames([])
    setResults(null)
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5" />
            {title}
          </DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        {dialogState === 'form' && (
          <>
            {selectedItems && selectedItems.length > 0 && (
              <div className="text-sm">
                {selectedItemsLabel && <div className="font-medium mb-1">{selectedItemsLabel}</div>}
                <div className="flex flex-wrap gap-1">
                  {selectedItems.map((item) => (
                    <span
                      key={item.id}
                      className="inline-flex items-center bg-secondary text-secondary-foreground rounded px-2 py-0.5 text-xs"
                    >
                      {item.name}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium">Select Clients</label>
                <p className="text-xs text-muted-foreground mb-2">
                  Choose which clients to {actionVerb.toLowerCase()} for
                </p>
                {clientsLoading ? (
                  <div className="flex items-center justify-center py-4">
                    <Spinner size="sm" />
                  </div>
                ) : (
                  <MultiSelect
                    options={clientOptions}
                    selected={selectedClientIds}
                    onChange={setSelectedClientIds}
                    placeholder="Search and select clients..."
                    searchPlaceholder={allowCreateClients ? 'Search or type to create...' : 'Search clients...'}
                    emptyMessage="No clients found."
                    allowCreate={allowCreateClients}
                    onCreateItem={
                      allowCreateClients
                        ? (name) => {
                            if (name && !manualClientNames.includes(name)) {
                              setManualClientNames((prev) => [...prev, name])
                            }
                          }
                        : undefined
                    }
                    manualItems={allowCreateClients ? manualClientNames : undefined}
                    onRemoveManualItem={
                      allowCreateClients
                        ? (name) => setManualClientNames((prev) => prev.filter((n) => n !== name))
                        : undefined
                    }
                  />
                )}
              </div>

              {formExtras}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button onClick={handleSubmit} disabled={totalClientCount === 0}>
                {actionVerb} for {totalClientCount} client{totalClientCount !== 1 ? 's' : ''}
              </Button>
            </DialogFooter>
          </>
        )}

        {dialogState === 'submitting' && (
          <div className="py-8 text-center">
            <Spinner size="lg" className="mx-auto mb-4" />
            <p className="text-sm text-muted-foreground">{submittingMessage ?? 'Working...'}</p>
            <p className="text-xs text-muted-foreground mt-2">This may take a few moments</p>
          </div>
        )}

        {dialogState === 'results' && results !== null && (
          <>
            {renderResults(results)}
            <DialogFooter>
              <Button onClick={handleClose}>Done</Button>
            </DialogFooter>
          </>
        )}

        {submitMutation.isError && dialogState === 'form' && (
          <div className="flex items-center gap-2 p-3 bg-destructive/10 text-destructive rounded-md text-sm">
            <AlertCircle className="h-4 w-4" />
            Request failed. Please try again.
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
